/**
 * Server functions لإدارة "بطاقات overlay على بلوكات Collections" — جدول
 * `collection_overlay_campaigns`.
 *
 * - getActiveCollectionOverlay({ handle, market }): عامّ runtime، يختار بطاقة
 *   واحدة فعّالة الآن لـ(collection_handle × market) مع fallback لـ"*".
 * - listCollectionOverlays(): أدمن، كلّ البطاقات مرتَّبة بـhandle ثم priority.
 * - upsertCollectionOverlay(payload): أدمن، إنشاء/تعديل.
 * - deleteCollectionOverlay({ id }): أدمن، حذف.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { assertAdmin } from "@/utils/adminAuth.server";

export const OVERLAY_MARKETS = ["*", "SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US"] as const;
export type OverlayMarket = (typeof OVERLAY_MARKETS)[number];

export type CollectionOverlay = {
  id: string;
  collection_handle: string;
  name: string;
  market: OverlayMarket;
  starts_at: string | null;
  ends_at: string | null;
  priority: number;
  enabled: boolean;
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_href: string | null;
  created_at: string;
  updated_at: string;
};

function normalizeMarket(m: string | undefined | null): OverlayMarket {
  if (!m) return "*";
  return (OVERLAY_MARKETS as readonly string[]).includes(m)
    ? (m as OverlayMarket)
    : "*";
}

/** Runtime: يختار overlay واحد لمجموعة معيّنة وسوق الزائر. */
export const getActiveCollectionOverlay = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        handle: z.string().min(1).max(160),
        market: z.string().optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const market = normalizeMarket(data.market);
    const nowIso = new Date().toISOString();

    const { data: rows, error } = await supabaseAdmin
      .from("collection_overlay_campaigns")
      .select(
        "id, market, priority, starts_at, ends_at, title_ar, title_en, subtitle_ar, subtitle_en, cta_label_ar, cta_label_en, cta_href",
      )
      .eq("collection_handle", data.handle)
      .eq("enabled", true)
      .in("market", [market, "*"])
      .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
      .or(`ends_at.is.null,ends_at.gt.${nowIso}`);

    if (error || !rows || rows.length === 0) return null;

    const sorted = [...rows].sort((a, b) => {
      if (a.market !== b.market) {
        if (a.market === market) return -1;
        if (b.market === market) return 1;
      }
      if (a.priority !== b.priority) return b.priority - a.priority;
      const at = a.starts_at ? new Date(a.starts_at).getTime() : 0;
      const bt = b.starts_at ? new Date(b.starts_at).getTime() : 0;
      return bt - at;
    });

    const r = sorted[0];
    return {
      id: r.id,
      title_ar: r.title_ar,
      title_en: r.title_en,
      subtitle_ar: r.subtitle_ar,
      subtitle_en: r.subtitle_en,
      cta_label_ar: r.cta_label_ar,
      cta_label_en: r.cta_label_en,
      cta_href: r.cta_href,
    };
  });

/** Runtime مجمَّع: يجلب overlay واحد لكلّ handles المعطاة دفعةً واحدة. */
export const getActiveCollectionOverlaysBulk = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        handles: z.array(z.string().min(1).max(160)).min(1).max(40),
        market: z.string().optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const market = normalizeMarket(data.market);
    const nowIso = new Date().toISOString();

    const { data: rows, error } = await supabaseAdmin
      .from("collection_overlay_campaigns")
      .select(
        "id, collection_handle, market, priority, starts_at, ends_at, title_ar, title_en, subtitle_ar, subtitle_en, cta_label_ar, cta_label_en, cta_href",
      )
      .in("collection_handle", data.handles)
      .eq("enabled", true)
      .in("market", [market, "*"])
      .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
      .or(`ends_at.is.null,ends_at.gt.${nowIso}`);

    if (error || !rows) return {} as Record<string, ReturnType<typeof pickOverlay>>;

    const grouped: Record<string, typeof rows> = {};
    for (const r of rows) {
      const h = r.collection_handle;
      if (!grouped[h]) grouped[h] = [] as unknown as typeof rows;
      grouped[h].push(r);
    }

    const out: Record<string, ReturnType<typeof pickOverlay>> = {};
    for (const [handle, list] of Object.entries(grouped)) {
      const sorted = [...list].sort((a, b) => {
        if (a.market !== b.market) {
          if (a.market === market) return -1;
          if (b.market === market) return 1;
        }
        if (a.priority !== b.priority) return b.priority - a.priority;
        const at = a.starts_at ? new Date(a.starts_at).getTime() : 0;
        const bt = b.starts_at ? new Date(b.starts_at).getTime() : 0;
        return bt - at;
      });
      out[handle] = pickOverlay(sorted[0]);
    }
    return out;
  });

function pickOverlay(r: {
  id: string;
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_href: string | null;
}) {
  return {
    id: r.id,
    title_ar: r.title_ar,
    title_en: r.title_en,
    subtitle_ar: r.subtitle_ar,
    subtitle_en: r.subtitle_en,
    cta_label_ar: r.cta_label_ar,
    cta_label_en: r.cta_label_en,
    cta_href: r.cta_href,
  };
}

export const listCollectionOverlays = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("collection_overlay_campaigns")
      .select("*")
      .order("collection_handle", { ascending: true })
      .order("priority", { ascending: false })
      .order("starts_at", { ascending: false, nullsFirst: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CollectionOverlay[];
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  collection_handle: z.string().min(1).max(160),
  name: z.string().min(1).max(120),
  market: z.enum(OVERLAY_MARKETS).default("*"),
  starts_at: z.preprocess((v) => {
    if (v === null || v === undefined || v === "") return null;
    const d = new Date(v as string);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }, z.string().nullable()),
  ends_at: z.preprocess((v) => {
    if (v === null || v === undefined || v === "") return null;
    const d = new Date(v as string);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }, z.string().nullable()),
  priority: z.number().int().min(0).max(1000).default(0),
  enabled: z.boolean().default(true),
  title_ar: z.string().max(160).nullable().optional(),
  title_en: z.string().max(160).nullable().optional(),
  subtitle_ar: z.string().max(400).nullable().optional(),
  subtitle_en: z.string().max(400).nullable().optional(),
  cta_label_ar: z.string().max(60).nullable().optional(),
  cta_label_en: z.string().max(60).nullable().optional(),
  cta_href: z.string().max(2048).nullable().optional(),
});

export const upsertCollectionOverlay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);

    const payload = {
      collection_handle: data.collection_handle.trim(),
      name: data.name.trim(),
      market: data.market,
      starts_at: data.starts_at || null,
      ends_at: data.ends_at || null,
      priority: data.priority ?? 0,
      enabled: data.enabled ?? true,
      title_ar: data.title_ar?.trim() || null,
      title_en: data.title_en?.trim() || null,
      subtitle_ar: data.subtitle_ar?.trim() || null,
      subtitle_en: data.subtitle_en?.trim() || null,
      cta_label_ar: data.cta_label_ar?.trim() || null,
      cta_label_en: data.cta_label_en?.trim() || null,
      cta_href: data.cta_href?.trim() || null,
    };

    if (data.id) {
      const { data: row, error } = await supabaseAdmin
        .from("collection_overlay_campaigns")
        .update(payload)
        .eq("id", data.id)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return row as CollectionOverlay;
    }

    const { data: row, error } = await supabaseAdmin
      .from("collection_overlay_campaigns")
      .insert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row as CollectionOverlay;
  });

export const deleteCollectionOverlay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin
      .from("collection_overlay_campaigns")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
